import React from 'react';
import { Crown, ShieldCheck, Mail, ExternalLink, Calendar, Users, Gamepad2, MessageSquare, CalendarClock, HeartHandshake } from 'lucide-react';
import { RatingBadge } from './RatingBadge';
import { RatingTrend } from './RatingTrend';
import { fmtDate, daysUntil, CATEGORY_LABELS } from './groupMeta';

const Card = ({ title, Icon, hex = '#00E5FF', children, testId }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5" data-testid={testId}>
    <p className="flex items-center gap-2 text-[9px] tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: hex }}><Icon className="w-3.5 h-3.5" />{title}</p>
    {children}
  </div>
);

const Row = ({ Icon, children }) => <p className="flex items-center gap-2 text-sm text-white/70"><Icon className="w-3.5 h-3.5 text-white/30" />{children}</p>;

const StaffContactBlock = ({ name, username, contact = {}, subject, testId }) => !username ? <p className="text-sm text-white/40">Not assigned</p> : (
  <div className="space-y-1.5">
    <p className="text-white font-medium" data-testid={`${testId}-name`}>{name}</p>
    <p className="text-[11px] text-white/40">@{username} · Starnight staff</p>
    {contact.roblox_username && <Row Icon={Gamepad2}>@{contact.roblox_username}</Row>}
    {contact.email && <a href={`mailto:${contact.email}?subject=${encodeURIComponent(subject)}`} data-testid={`${testId}-email-link`} className="flex items-center gap-2 text-xs text-cyan-200/80 hover:text-cyan-100"><Mail className="w-3.5 h-3.5" />{contact.email}</a>}
  </div>
);

export const GroupOverviewTab = ({ group: g }) => {
  const latest = g.inspections?.[0];
  const due = daysUntil(g.next_inspection_due);
  const l = g.leader || {};
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="group-overview-tab">
      <div className="lg:col-span-2 space-y-4">
        <Card title="About" Icon={Users}>
          <p className="text-sm text-white/70 whitespace-pre-line leading-relaxed">{g.description || 'No description yet.'}</p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
            <div className="p-3 rounded-xl bg-black/30"><p className="text-white/40 text-[10px]">Founded</p><p className="text-white mt-0.5">{fmtDate(g.founded_date)}</p></div>
            <div className="p-3 rounded-xl bg-black/30"><p className="text-white/40 text-[10px]">Approx. members</p><p className="text-white mt-0.5">{g.member_count ?? '—'}</p></div>
            <div className="p-3 rounded-xl bg-black/30"><p className="text-white/40 text-[10px]">Staff listed</p><p className="text-white mt-0.5">{(g.members || []).length}</p></div>
          </div>
        </Card>
        <Card title="Latest inspection" Icon={CalendarClock} hex="#A78BFA" testId="latest-inspection-card">
          {latest ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3"><RatingBadge rating={latest.rating} size="lg" /><span className="text-sm text-white/60">{fmtDate(latest.date)} · {latest.inspector_name}</span></div>
              {latest.summary && <p className="text-sm text-white/70">{latest.summary}</p>}
              {Object.keys(latest.scores || {}).length > 0 && (
                <div className="space-y-1.5">{Object.entries(latest.scores).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3 text-[11px]"><span className="w-36 text-white/55">{CATEGORY_LABELS[k]}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-white/[0.05]"><div className="h-full rounded-full bg-gradient-to-r from-violet-400 to-cyan-400" style={{ width: `${v * 20}%` }} /></div>
                    <span className="text-white/50 tabular-nums">{v}/5</span></div>
                ))}</div>
              )}
              <RatingTrend inspections={g.inspections} />
            </div>
          ) : <p className="text-sm text-white/40">This group hasn't been inspected yet.</p>}
        </Card>
      </div>
      <div className="space-y-4">
        <Card title="Appointed group leader" Icon={Crown} hex="#F59E0B" testId="leader-card">
          {l.name ? (
            <div className="space-y-2">
              <p className="text-lg text-white font-semibold">{l.name}</p>
              {l.roblox_username && <Row Icon={Gamepad2}>@{l.roblox_username}</Row>}
              {l.discord && <Row Icon={MessageSquare}>{l.discord}</Row>}
              {l.appointed_date && <Row Icon={Calendar}>Appointed {fmtDate(l.appointed_date)}</Row>}
              {l.email && (l.email.includes('•') ? <Row Icon={Mail}>{l.email}</Row> : (
                <a href={`mailto:${l.email}?subject=${encodeURIComponent(`${g.name} — Starnight Holdings`)}`} data-testid="leader-email-link"
                  className="mt-2 flex items-center justify-center gap-2 py-2 rounded-xl border border-amber-400/30 bg-amber-500/10 text-amber-100 text-xs hover:bg-amber-500/20"><Mail className="w-3.5 h-3.5" /> Email {l.email}</a>
              ))}
            </div>
          ) : <p className="text-sm text-white/40">No leader appointed.</p>}
        </Card>
        <Card title="Group oversight" Icon={ShieldCheck} testId="oversight-card">
          <StaffContactBlock name={g.oversight_name} username={g.oversight_username} contact={g.oversight_contact} subject={g.name} testId="oversight" />
          {g.next_inspection_due && g.status !== 'archived' && (
            <p className={`mt-3 text-xs ${due < 0 ? 'text-rose-300' : due <= 14 ? 'text-amber-300' : 'text-white/55'}`}>Next inspection {fmtDate(g.next_inspection_due)} {due < 0 ? `(${-due} days overdue)` : `(in ${due} days)`}</p>
          )}
        </Card>
        <Card title="People partner" Icon={HeartHandshake} hex="#A78BFA" testId="people-partner-card">
          <StaffContactBlock name={g.people_partner_name} username={g.people_partner_username} contact={g.people_partner_contact} subject={g.name} testId="people-partner" />
        </Card>
        {(g.links || []).length > 0 && (
          <Card title="Links" Icon={ExternalLink} hex="#10B981" testId="links-card">
            <div className="space-y-1.5">{g.links.map((k, i) => (
              <a key={i} href={k.url} target="_blank" rel="noopener noreferrer" data-testid={`group-link-${i}`}
                className="flex items-center justify-between px-3 py-2 rounded-xl bg-black/30 border border-white/[0.05] text-xs text-white/75 hover:text-white hover:border-emerald-400/30">
                {k.label}<ExternalLink className="w-3 h-3" /></a>
            ))}</div>
          </Card>
        )}
      </div>
    </div>
  );
};
